import React, { useState, Component } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';
import { MyceliumNetworkHero } from '../components/MyceliumNetworkHero';
import { DNAHelixHero } from '../components/DNAHelixHero';
interface TeamMember {
  name: string;
  role: string;
  story: string;
  background: string;
  image: string;
}
interface AdvisorMember {
  name: string;
  role: string;
  bio: string;
}
interface CommitteeMember {
  name: string;
  bio: string;
}
export function TeamSplitScreen() {
  const [selectedMember, setSelectedMember] = useState<{
    type: 'core' | 'advisor' | 'committee';
    index: number;
  } | null>(null);
  const {
    scrollYProgress
  } = useScroll();
  const coreTeam: TeamMember[] = [{
    name: 'Natalya Wallin',
    role: 'GP & Managing Director',
    story: "I am a high conviction leader. I see opportunity in chaos and go after seemingly intransigent challenges. That is encoded in my DNA - my grandparents were shipped to Siberian labor camps and came to the United States as refugees to start over. So I come by my relentless drive and endurance honestly. I spent 10 years building and deploying a $75M global fund in the face of political headwinds. I successfully completed the Moab 240-miler (the brutal race that former Navy SEAL David Goggins DNF'd on his first attempt).",
    background: '10+ years experience fundraising, organization building, and fund management. Raised $75M from family offices, foundations, and governments to address systemic drivers of forced labor risks in target sectors.',
    image: 'https://cdn.magicpatterns.com/uploads/nPrbWRd99BLA7hL4gLSpQo/7_Natalya.jpg'
  }, {
    name: 'Patrick Livingston',
    role: 'Built Environment Expert & Portfolio Construction',
    story: "I solve high-stakes engineering and business challenges, from delivering multimillion-dollar infrastructure projects to scaling high-growth teams. I don't walk away from tough problems - I have spent my entire career at the same firm, seeing projects through from inception to construction, even when they were politically sensitive or on impossible timelines. I have designed infrastructure to withstand hurricanes, rebuilt critical systems in the wake of climate disasters.",
    background: '12+ years of experience in engineering, infrastructure, and business leadership. Delivered more than $1 billion in constructed infrastructure, solving complex technical, regulatory, and financial challenges to bring large-scale projects from concept to completion.',
    image: 'https://cdn.magicpatterns.com/uploads/oagLnQyesCH4gCMgV853L2/Pat_Headshot.jpg'
  }, {
    name: 'Rollin Nash',
    role: 'Operations & Resource Planning Specialist',
    story: 'I have spent most of my professional career in professions and sectors where my decisions were quite literally life and death. I excel in high stress, ambiguous situations for two reasons: First, I have an innate ability to stay calm under pressure, see the big picture, and direct efforts accordingly. Second, I create organizations that are ruthlessly efficient. Everyone knows what to do, how it fits into the bigger picture, why it matters, and when to make decisions.',
    background: '12+ years of experience in project management, operations, strategic planning, finance & back office. Planned, resourced, and implemented several multi-million dollar strategic initiatives including First-in-Human clinical trials, proof-of-concept military operations, and organizational transformations.',
    image: 'https://cdn.magicpatterns.com/uploads/aQWr6mTKqjSUTCytDF8GyV/Headshot.jpg'
  }];
  const advisors: AdvisorMember[] = [{
    name: 'Melissa Kim',
    role: 'Advisor & Original Co-founder',
    bio: 'Experienced executive, entrepreneur, and Vitality original co-founder'
  }, {
    name: 'David Balkcom',
    role: 'Advisor, Finance & Agriculture',
    bio: 'Managing Director FTI Consulting, expertise in controlled environment agriculture, turnaround and transformation'
  }, {
    name: 'Dr. Jean Baderschneider',
    role: 'Advisor, Investor Relations & Climate Finance',
    bio: '35 years international experience, non-executive Director Fortescue Metals Group, former VP Global Procurement ExxonMobil'
  }, {
    name: 'Matt Esposito',
    role: 'Advisor',
    bio: 'Strategic advisor with deep expertise in venture capital and startup ecosystems.'
  }];
  const committee: CommitteeMember[] = [{
    name: 'Jordan McAlister',
    bio: 'Head of Investment & Innovation Strategy with Hyperspace Ventures'
  }, {
    name: 'Kate Knight',
    bio: 'Executive scaling coach and advisor with 20 years of experience'
  }, {
    name: 'Robin Berry',
    bio: 'Director of Lab to Market Innovation at ASU Biomimicry Center'
  }, {
    name: 'Olivia Hanrahan-Soar',
    bio: 'Expert in innovative finance and impact investing'
  }, {
    name: 'Medina Korda Poole',
    bio: 'High-impact leader managing $150M+ portfolios globally'
  }, {
    name: 'Chris Burton',
    bio: 'Experienced investor and operator with a focus on sustainable technologies.'
  }];
  return <div className="min-h-screen bg-black" data-id="element-1648">
      {/* Progress Indicator */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-vitalityTeal z-50 origin-left" style={{
      scaleX: scrollYProgress
    }} data-id="element-1649" />

      {/* Hero Intro */}
      <section className="h-screen flex items-center justify-center relative overflow-hidden" data-id="element-1650">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e2533] to-black" data-id="element-1651" />

        {/* Background image - mycelium network microscope */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576086213369-97a306d36557?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-15" data-id="element-1652" />

        {/* Emphasized SVG overlay */}
        <div className="absolute inset-0 opacity-60" data-id="element-1653">
          <MyceliumNetworkHero data-id="element-1654" />
        </div>

        <div className="relative z-10 text-center px-4" data-id="element-1655">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-6xl md:text-8xl font-bold text-white mb-6" data-id="element-1656">
            Our Team
          </motion.h1>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="flex justify-center" data-id="element-1657">
            <ChevronDown className="w-8 h-8 text-vitalityTeal animate-bounce" data-id="element-1658" />
          </motion.div>
        </div>
      </section>

      {/* Core Team - Full Viewport Sections */}
      {coreTeam.map((member, index) => <SplitScreenSection key={member.name} member={member} index={index} imageOnLeft={index % 2 === 0} onReadMore={() => setSelectedMember({
      type: 'core',
      index
    })} data-id="element-1659" />)}

      {/* Advisors - Compact Section */}
      <section className="min-h-screen flex items-center bg-gradient-to-b from-black to-[#0e2533] py-20" data-id="element-1660">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-1661">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-16" data-id="element-1662">
            <h2 className="text-5xl font-bold text-white mb-4" data-id="element-1663">Advisors</h2>
            <p className="text-gray-400 text-xl" data-id="element-1664">
              Strategic guidance from industry veterans
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-1665">
            {advisors.map((advisor, index) => <motion.div key={advisor.name} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-1666">
                <div className="flex items-start gap-6" data-id="element-1667">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#a0e6e9]/20 to-[#a0e6e9]/5 flex items-center justify-center flex-shrink-0 border-2 border-[#a0e6e9]/30" data-id="element-1668">
                    <span className="text-3xl font-bold text-[#a0e6e9]/70" data-id="element-1669">
                      {advisor.name[0]}
                    </span>
                  </div>
                  <div data-id="element-1670">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1671">
                      {advisor.name}
                    </h3>
                    <p className="text-[#a0e6e9] text-sm font-medium mb-3" data-id="element-1672">
                      {advisor.role}
                    </p>
                    <p className="text-gray-400" data-id="element-1673">{advisor.bio}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Committee - Simple List */}
      <section className="py-20 bg-[#0e2533]" data-id="element-1674">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1675">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-12" data-id="element-1676">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-1677">
              Investment Review Committee
            </h2>
            <p className="text-gray-500 text-lg" data-id="element-1678">
              Expert evaluation and due diligence
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1679">
            {committee.map((member, index) => <motion.div key={member.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="p-6 rounded-xl bg-white/5 border border-white/10" data-id="element-1680">
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-1681">
                  {member.name}
                </h3>
                <p className="text-gray-500 text-sm" data-id="element-1682">{member.bio}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence data-id="element-1683">
        {selectedMember !== null && <motion.div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSelectedMember(null)} data-id="element-1684">
            <motion.div className="bg-[#1c3f4d] rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto border-2 border-vitalityTeal/30 relative" initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} data-id="element-1685">
              <button onClick={() => setSelectedMember(null)} className="absolute top-6 right-6 text-white hover:text-vitalityTeal transition-colors" data-id="element-1686">
                <X className="w-6 h-6" data-id="element-1687" />
              </button>
              <div className="p-10" data-id="element-1688">
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-2" data-id="element-1689">
                  {coreTeam[selectedMember.index].role}
                </p>
                <h3 className="text-4xl font-bold text-white mb-8" data-id="element-1690">
                  {coreTeam[selectedMember.index].name}
                </h3>
                <div className="space-y-6" data-id="element-1691">
                  <div data-id="element-1692">
                    <h4 className="text-white font-bold text-xl mb-3" data-id="element-1693">Story</h4>
                    <p className="text-gray-300 leading-relaxed text-lg" data-id="element-1694">
                      {coreTeam[selectedMember.index].story}
                    </p>
                  </div>
                  <div data-id="element-1695">
                    <h4 className="text-white font-bold text-xl mb-3" data-id="element-1696">
                      Background
                    </h4>
                    <p className="text-gray-300 leading-relaxed text-lg" data-id="element-1697">
                      {coreTeam[selectedMember.index].background}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Split Screen Section Component
function SplitScreenSection({
  member,
  index,
  imageOnLeft,
  onReadMore
}: {
  member: TeamMember;
  index: number;
  imageOnLeft: boolean;
  onReadMore: () => void;
}) {
  const {
    scrollYProgress
  } = useScroll();
  const imageY = useTransform(scrollYProgress, [index * 0.25, (index + 1) * 0.25], ['20%', '-20%']);
  return <section className="min-h-screen flex items-center relative overflow-hidden bg-black" data-id="element-1698">
      <div className="w-full" data-id="element-1699">
        <div className="grid lg:grid-cols-2 min-h-screen" data-id="element-1700">
          {/* Image Side */}
          <motion.div className={`relative ${imageOnLeft ? 'lg:order-1' : 'lg:order-2'}`} initial={{
          opacity: 0,
          x: imageOnLeft ? -50 : 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1701">
            <motion.div className="absolute inset-0" style={{
            y: imageY
          }} data-id="element-1702">
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent z-10" data-id="element-1703" />
              <img src={member.image} alt={member.name} className="w-full h-full object-cover" style={{
              objectPosition: 'center 20%'
            }} data-id="element-1704" />
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <motion.div className={`flex items-center px-8 md:px-16 py-20 ${imageOnLeft ? 'lg:order-2' : 'lg:order-1'}`} initial={{
          opacity: 0,
          x: imageOnLeft ? 50 : -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-1705">
            <div data-id="element-1706">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4
            }} className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-1707">
                {member.role}
              </motion.p>

              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5
            }} className="text-5xl md:text-6xl font-bold text-white mb-8" data-id="element-1708">
                {member.name}
              </motion.h2>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.6
            }} className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8" data-id="element-1709">
                {member.story}
              </motion.p>

              <motion.button initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.7
            }} onClick={onReadMore} className="inline-flex items-center gap-2 px-6 py-3 bg-vitalityTeal/10 hover:bg-vitalityTeal/20 border border-vitalityTeal/30 rounded-lg text-vitalityTeal font-semibold transition-colors" data-id="element-1710">
                Read Full Background
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}